import { useCallback, useEffect, useRef, useState } from 'react';

import { getPreferencesAvailableDates } from 'PrivateAPI';

import { isApplicationNonProd } from 'Utilities/helpers';
import { safeLocalStorage } from 'Utilities/storages';

type AvailableDates = string[] | undefined;

export type UseGetAvailability = {
  availableDates: AvailableDates;
  loading: boolean;
  refresh: () => void;
}

const performMockIfNotOnProduction: any = async (cb: () => Promise<any>) => {
  const result = await cb();
  if (isApplicationNonProd) {
    const notAvailableDates = JSON.parse(safeLocalStorage.getItem('scpDatesNotAvailableArray') || '[]');
    // remove any dates present in the notAvailableDates array from the result.dates.availableDays array
    result.data.availableDays = result.data.availableDays.filter((date: string) => !notAvailableDates.includes(date));
  }
  return result;
};

export const useGetAvailability = (): UseGetAvailability => {
  const [loading, setLoading] = useState<boolean>(false);
  const [availableDates, setAvailableDates] = useState<AvailableDates>();
  const [refresh, setRefresh] = useState(0);
  const cancelled = useRef(false);

  const resolve = (data: AvailableDates) => {
    if (cancelled.current) {
      return;
    }
    setAvailableDates(data);
  };

  const reject = (err: Error) => {
    window?.Sentry?.captureException?.(err);
  };

  const getAvailability = useCallback(async () => {
    setLoading(true);

    try {
      const dates = await performMockIfNotOnProduction(getPreferencesAvailableDates);
      resolve(dates?.data?.availableDays);
    } catch (e) {
      reject(e as Error);
      setAvailableDates([]);
    } finally {
      if (!cancelled.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => () => {
    cancelled.current = true;
  }, []);

  useEffect(() => {
    (async () => {
      getAvailability();
    })();
  }, [getAvailability, refresh]);

  return {
    availableDates,
    loading,
    // This will refresh the dates by changing the refresh value and running the useEffect again
    refresh: () => setRefresh(refresh + 1),
  };
};

export default useGetAvailability;
